import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import useToast from '../../hooks/useToast';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import '../FullViewLayout.css';
import './ViewSaleModalStyles.css';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import ConfirmModal from '../ConfirmModal';
import ContactPurchasesModal from './ContactPurchasesModal';
import LedgerModal from './LedgerModal';
import dateUtils from '../../utils/dateUtils';

const DL = ({ label, value, span }) => (
  <div style={{ gridColumn: span ? 'span 2' : 'span 1' }}>
    <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
    <div style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.92rem', wordBreak: 'break-word' }}>{value || <span style={{ color: '#94a3b8' }}>-</span>}</div>
  </div>
);
const SH = ({ children }) => (
  <div style={{ gridColumn: 'span 2', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.35rem', marginTop: '0.5rem', marginBottom: '0.25rem', fontWeight: 700, fontSize: '0.82rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{children}</div>
);
const Customers = ({ isActive, filterStatus, searchTerm, onSearchChange, onShowFilters }) => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: '', data: null });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalEntries, setTotalEntries] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [isClientPaginated, setIsClientPaginated] = useState(false);

    // Filter States removed (lifted to parent)

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [openActionId, setOpenActionId] = useState(null);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [showLedgerModal, setShowLedgerModal] = useState(false);
    const [showDocModal, setShowDocModal] = useState(false);
    const [showPurchasesModal, setShowPurchasesModal] = useState(false);
    const [payAmount, setPayAmount] = useState('');
    const [fetchTrigger, setFetchTrigger] = useState(0);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [payNote, setPayNote] = useState('');
    const [docNote, setDocNote] = useState('');
    const [payMethod, setPayMethod] = useState('Cash');
    const [payAccount, setPayAccount] = useState('');
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [isLoadingPayAccounts, setIsLoadingPayAccounts] = useState(false);
    const [isLoadingPayMethods, setIsLoadingPayMethods] = useState(false);

    const handleExport = (format) => {
        if (!customers || customers.length === 0) {
            alert('No customers to export');
            setShowExportDropdown(false);
            return;
        }

        const headers = ['Contact ID', 'Business Name', 'Name', 'Email', 'Mobile', 'Tax Number', 'Credit Limit', 'Balance'];
        const rows = customers.map(c => [
            c.contact_id || '', c.business_name || '', c.name || '', c.email || '', c.mobile || '',
            c.tax_number || '', c.credit_limit || '0', c.balance || '0'
        ]);

        const title = 'Customers List';
        const filename = 'customers';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();

        setShowExportDropdown(false);
    };

    const handlePrint = () => { printPage(); setShowExportDropdown(false); };
    const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };
    const handleExportPDF = () => { handleExport('PDF'); };

    // Debounce search input so API is not hit on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when debounced search or filter changes, then trigger a single fetch
    useEffect(() => {
        setCurrentPage(1);
        setFetchTrigger(t => t + 1);
    }, [debouncedSearchTerm, filterStatus]);

    useEffect(() => {
        if (isActive) {
            fetchCustomers();
        }
    }, [isActive, currentPage, perPage, fetchTrigger]);

    const fetchCustomers = async () => {
        setIsLoading(true);
        try {
            const params = {
                type: 'customer',
                per_page: -1
            };
            if (debouncedSearchTerm.trim()) {
                params.name = debouncedSearchTerm.trim();
            }
            const data = await apiService.getContacts(params);
            const customerList = Array.isArray(data) ? data : (data.data || []);
            setCustomers(customerList);
            setIsClientPaginated(true);
            setTotalEntries(customerList.length);
            setLastPage(Math.ceil(customerList.length / perPage));
        } catch (err) {
      /* console.error('Failed to load customers', err); */
            setError('Failed to load customers');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredCustomers = useMemo(() => {
        let result = [...customers];
        if (filterStatus !== 'all') {
            result = result.filter(c => c.contact_status === filterStatus);
        }
        if (searchTerm.trim()) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(c => {
                const fields = [
                    c.name,
                    c.supplier_business_name,
                    c.business_name,
                    c.email,
                    c.mobile,
                    c.contact_id,
                    c.tax_number,
                    c.city,
                    c.state,
                    c.address_line_1,
                    c.landline,
                    c.alternate_number,
                    c.custom_field1,
                    c.custom_field2,
                    c.custom_field3,
                    c.custom_field4,
                    c.custom_field5
                ];
                return fields.some(f => f && String(f).toLowerCase().includes(lower));
            });
        }
        return result;
    }, [customers, searchTerm, filterStatus]);
    const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

    const toggleActionMenu = (id, event) => {
        if (event) event.stopPropagation();
        if (openActionId === id) {
            setOpenActionId(null);
        } else {
            const rect = event ? event.currentTarget.getBoundingClientRect() : { bottom: 0, top: 0, left: 0 };
            const gap = 8;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const menuMinWidth = 220;
            const edgePadding = 8;

            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
            const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
            const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));

            const left = Math.min(
                Math.max(rect.left, edgePadding),
                Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding)
            );

            setDropdownPosition({
                top: openUpwards ? null : (rect.bottom + gap),
                bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
                left,
                maxHeight
            });
            setOpenActionId(id);
        }
    };

    const handleViewCustomer = async (customer) => {
        if (!customer) return;
        setSelectedCustomer(customer);
        setShowViewModal(true);
        setOpenActionId(null);
        try {
            const data = await apiService.getCustomerById(customer.id);
            let detail = data;
            if (data?.data) {
                detail = Array.isArray(data.data) ? data.data[0] : data.data;
            } else if (Array.isArray(data)) {
                detail = data[0];
            }
            if (detail) {
                setSelectedCustomer(prev => ({ ...prev, ...detail }));
            }
        } catch (e) {}
    };

    const handleCloseModal = () => {
        setShowViewModal(false);
        setSelectedCustomer(null);
    };

    const handleEditCustomer = (id) => {
        navigate(`/contacts/customers/edit/${id}`);
    };

    const handleDeleteCustomer = (id) => {
        setOpenActionId(null);
        setConfirmModal({ isOpen: true, action: 'delete', data: id });
    };

    const handleDeactivate = (customer) => {
        setOpenActionId(null);
        setConfirmModal({ isOpen: true, action: 'deactivate', data: customer });
    };

    const confirmAction = async () => {
        const { action, data } = confirmModal;
        setConfirmModal({ isOpen: false, action: '', data: null });

        if (action === 'delete') {
            try {
                await apiService.deleteContact(data);
                fetchCustomers();
                showSuccess('Customer deleted successfully!');
            } catch (err) {
                showError('Failed to delete customer: ' + err.message);
            }
        } else if (action === 'deactivate') {
            const customer = data;
            const newStatus = customer.contact_status === 'inactive' ? 'active' : 'inactive';
            try {
                await apiService.updateContact(customer.id, { contact_status: newStatus });
                // Update local state immediately so it stays in the list with the updated status
                setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, contact_status: newStatus } : c));
                
                if (newStatus === 'inactive') {
                    showSuccess('Customer deactivated successfully!');
                } else {
                    showSuccess('Customer activated successfully!');
                }
            } catch (err) { 
                showError('Failed: ' + err.message); 
            }
        }
    };

    const openPayModal = async (customer) => {
        setSelectedCustomer(customer);
        setPayAmount('');
        setPayNote('');
        setPayMethod('Cash');
        setPayAccount('');
        setShowPayModal(true);
        setOpenActionId(null);

        try {
            setIsLoadingPayAccounts(true);
            const accountsData = await apiService.getPaymentAccounts({ per_page: -1 });
            setPaymentAccounts(accountsData.data || accountsData || []);
        } catch (e) {
            setPaymentAccounts([]);
        } finally {
            setIsLoadingPayAccounts(false);
        }

        try {
            setIsLoadingPayMethods(true);
            const methodsData = await apiService.getPaymentMethods();
            let list = [];
            if (typeof methodsData === 'object' && methodsData !== null && !Array.isArray(methodsData)) {
                list = Object.entries(methodsData)
                    .filter(([k, v]) => !k.startsWith('custom_pay') || (typeof v === 'string' && !v.toLowerCase().includes('custom payment')))
                    .map(([k, v]) => ({ id: k, name: typeof v === 'string' ? v : (v.name || k) }));
            } else if (Array.isArray(methodsData?.data)) {
                list = methodsData.data.map(m => ({ id: m.id || m.key || m.name, name: m.name || m.label || String(m.id) }));
            } else if (Array.isArray(methodsData)) {
                list = methodsData.map(m => ({ id: m.id || m.key || m.name, name: m.name || m.label || String(m.id) }));
            }
            if (list.length === 0) {
                list = [{ id: 'Cash', name: 'Cash' }, { id: 'Card', name: 'Card' }, { id: 'Check', name: 'Check' }, { id: 'Bank Transfer', name: 'Bank Transfer' }];
            }
            setPaymentMethods(list);
        } catch (e) {
            setPaymentMethods([{ id: 'Cash', name: 'Cash' }, { id: 'Card', name: 'Card' }, { id: 'Check', name: 'Check' }, { id: 'Bank Transfer', name: 'Bank Transfer' }]);
        } finally {
            setIsLoadingPayMethods(false);
        }
    };
    const openLedger = (customer) => { setSelectedCustomer(customer); setShowLedgerModal(true); setOpenActionId(null); };
    const openDocModal = (customer) => { setSelectedCustomer(customer); setDocNote(''); setShowDocModal(true); setOpenActionId(null); };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown-container')) {
                setOpenActionId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedCustomers = (list) => {
        if (!sortConfig.key) return list;
        return [...list].sort((a, b) => {
            let aVal = a[sortConfig.key] || '';
            let bVal = b[sortConfig.key] || '';
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    return (
        <div className="full-view-inner">
            {/* Table Controls (Search, Entries) */}
            <div className="table-controls">
                <div className="table-controls-left">
                    <div className="entries-dropdown">
                        <span>Show</span>
                        <select value={perPage} onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                    </div>
                </div>

                <div className="table-controls-right">
                    <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="text"
                            placeholder="Search Customers..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="tabs-search-input"
                        />
                        <button
                            className="filter-toggle-btn"
                            onClick={onShowFilters}
                            title="Open Filters"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            Filters
                        </button>
                    </div>
                    <div className="export-dropdown-container" style={{ position: 'relative' }}>
                        <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Export ▾
                        </button>
                        {showExportDropdown && (
                            <div className="export-dropdown-menu">
                                <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                                <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                                <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                                <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                            </div>
                        )}
                    </div>
                    <button className="control-btn-primary" onClick={() => navigate('/contacts/customers/add')}>
                        <span className="plus-icon">+</span> Add
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="products-table-container">
                    {isLoading ? (
                        <div className="loading-sales">
                            <h3>⏳ Loading Customers...</h3>
                            <p>Fetching customers data from server...</p>
                        </div>
                    ) : filteredCustomers.length === 0 ? (
                        <div className="full-view-empty">
                            <h3>📊 No customers found</h3>
                            <p>Try adjusting filters or add a new customer.</p>
                        </div>
                    ) : (
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    {[
                                        { key: 'contact_id', label: 'Contact ID' },
                                        { key: 'business_name', label: 'Business Name' },
                                        { key: 'name', label: 'Name' },
                                        { key: 'email', label: 'Email', className: 'expand-column' },
                                        { key: 'mobile', label: 'Mobile' },
                                        { key: 'tax_number', label: 'Tax Number' },
                                        { key: 'credit_limit', label: 'Credit Limit' },
                                        { key: 'balance', label: 'Balance' }
                                    ].map(({ key, label, className }) => (
                                        <th key={key} className={`sortable ${className || ''}`} onClick={() => handleSort(key)}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                                {label}
                                                {sortConfig.key === key ? (
                                                    sortConfig.direction === 'asc' ? (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M18 15l-6-6-6 6" />
                                                        </svg>
                                                    ) : (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M6 9l6 6 6-6" />
                                                        </svg>
                                                    )
                                                ) : (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M7 15l5 5 5-5" />
                                                        <path d="M7 9l5-5 5 5" />
                                                    </svg>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCustomers.length === 0 ? (
                                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No customers found.</td></tr>
                                ) : (
                                    (() => {
                                        const sorted = getSortedCustomers(filteredCustomers);
                                        const displayList = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);
                                        return displayList.map(customer => (
                                            <tr key={customer.id}>
                                                <td className="actions-cell">
                                                    <div className="action-dropdown-container">
                                                        <button className="action-btn-trigger" onClick={(e) => toggleActionMenu(customer.id, e)}>
                                                            Actions <span className="arrow-down">▼</span>
                                                        </button>
                                                        {openActionId === customer.id && ReactDOM.createPortal(
                                                            <div
                                                                className="action-dropdown-menu"
                                                                style={{
                                                                    position: 'fixed',
                                                                    top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                                    bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                                    left: `${dropdownPosition.left}px`,
                                                                    maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                                                    zIndex: 9999999
                                                                }}
                                                            >
                                                                <button onClick={() => openPayModal(customer)}><i className="fa fa-credit-card" style={{ marginRight: '0.5rem', color: '#6366f1' }}></i> Pay</button>
                                                                <button onClick={() => handleViewCustomer(customer)}><i className="fa fa-eye" style={{ marginRight: '0.5rem', color: '#3b82f6' }}></i> View</button>
                                                                <button onClick={() => handleEditCustomer(customer.id)}><i className="fa fa-edit" style={{ marginRight: '0.5rem', color: '#10b981' }}></i> Edit</button>
                                                                <button onClick={() => handleDeleteCustomer(customer.id)}><i className="fa fa-trash" style={{ marginRight: '0.5rem', color: '#ef4444' }}></i> Delete</button>
                                                                <button onClick={() => handleDeactivate(customer)}><i className="fa fa-power-off" style={{ marginRight: '0.5rem', color: '#f59e0b' }}></i> {customer.contact_status === 'inactive' ? 'Activate' : 'Deactivate'}</button>
                                                                <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                                                                <button onClick={() => openLedger(customer)}><i className="fa fa-book" style={{ marginRight: '0.5rem', color: '#8b5cf6' }}></i> Ledger</button>
                                                                <button onClick={() => { setSelectedCustomer(customer); setShowPurchasesModal(true); setOpenActionId(null); }}><i className="fa fa-shopping-bag" style={{ marginRight: '0.5rem', color: '#0891b2' }}></i> Purchases</button>
                                                                <button onClick={() => openDocModal(customer)}><i className="fa fa-paperclip" style={{ marginRight: '0.5rem', color: '#dc2626' }}></i> Documents &amp; Note</button>
                                                            </div>,
                                                            document.body
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{customer.contact_id}</td>
                                                <td>
                                                    <div style={{ fontWeight: '500' }}>{customer.supplier_business_name || customer.business_name || '--'}</div>
                                                    {customer.contact_status === 'inactive' && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '700', marginTop: '4px' }}>(Deactivated)</div>}
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: '500' }}>{customer.name || '--'}</div>
                                                    {customer.contact_status === 'inactive' && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '700', marginTop: '4px' }}>(Deactivated)</div>}
                                                </td>
                                                <td className="expand-column">{customer.email || '--'}</td>
                                                <td>{customer.mobile}</td>
                                                <td>{customer.tax_number || '--'}</td>
                                                <td>{customer.credit_limit ? `$${Number(customer.credit_limit).toFixed(2)}` : '--'}</td>
                                                <td>{customer.balance ? `$${Number(customer.balance).toFixed(2)}` : '$0.00'}</td>
                                            </tr>
                                        ));
                                    })()
                                )}
                            </tbody>
                        </table>
                    )}
            </div>

            {/* Pagination */}
            {filteredCustomers.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, filteredCustomers.length)} of {filteredCustomers.length} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                            Page {currentPage} of {lastPage || 1}
                        </span>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                            disabled={currentPage === Math.ceil(filteredCustomers.length / perPage)}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* ── View Modal ── */}
            {showViewModal && selectedCustomer && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-container invoice-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>View Customer — <span style={{ color: '#6366f1' }}>{selectedCustomer.supplier_business_name || selectedCustomer.business_name || selectedCustomer.name}</span></h2>
                            <button className="modal-close-btn" onClick={handleCloseModal}>✕</button>
                        </div>
                        <div className="modal-content invoice-content" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {/* Main info row */}
                                <div className="customer-service-section">
                                    <div className="customer-info-box">
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem', color: 'var(--etpos-primary)' }}>Basic Info</h3>
                                        <div className="info-item">
                                            <strong>Contact ID</strong>
                                            <div>{selectedCustomer.contact_id || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Full Name</strong>
                                            <div>{selectedCustomer.name || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Business Name</strong>
                                            <div>{selectedCustomer.supplier_business_name || selectedCustomer.business_name || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Tax Number</strong>
                                            <div>{selectedCustomer.tax_number || '--'}</div>
                                        </div>
                                    </div>
                                    <div className="service-info-box">
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem', color: 'var(--etpos-primary)' }}>Account Status</h3>
                                        <div className="info-item">
                                            <strong>Status</strong>
                                            <div>
                                                <span style={{ 
                                                    padding: '0.2rem 0.6rem', 
                                                    borderRadius: '4px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: '700',
                                                    backgroundColor: selectedCustomer.contact_status === 'inactive' ? '#fee2e2' : '#dcfce7',
                                                    color: selectedCustomer.contact_status === 'inactive' ? '#991b1b' : '#166534'
                                                }}>
                                                    {(selectedCustomer.contact_status || 'active').toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Type / Sub Type</strong>
                                            <div>{selectedCustomer.type || 'Customer'} {selectedCustomer.sub_type ? `/ ${selectedCustomer.sub_type}` : ''}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Added On</strong>
                                            <div>{selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString() : '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Details */}
                                <div className="invoice-section">
                                    <h3>Contact Details</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Mobile</strong>
                                            <div>{selectedCustomer.mobile || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Alternate Number</strong>
                                            <div>{selectedCustomer.alternate_number || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Landline</strong>
                                            <div>{selectedCustomer.landline || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Email</strong>
                                            <div>{selectedCustomer.email || '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financial Info */}
                                <div className="invoice-section">
                                    <h3>Financial Information</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Opening Balance</strong>
                                            <div>{selectedCustomer.opening_balance != null ? `$${Number(selectedCustomer.opening_balance).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Advance Balance</strong>
                                            <div>{selectedCustomer.advance_balance != null ? `$${Number(selectedCustomer.advance_balance).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Current Balance</strong>
                                            <div style={{ fontWeight: 700, color: selectedCustomer.balance > 0 ? '#ef4444' : '#166534' }}>
                                                {`$${Number(selectedCustomer.balance || 0).toFixed(2)}`}
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Credit Limit</strong>
                                            <div>{selectedCustomer.credit_limit != null ? `$${Number(selectedCustomer.credit_limit).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Total Sell Due</strong>
                                            <div>{selectedCustomer.sell_due != null ? `$${Number(selectedCustomer.sell_due).toFixed(2)}` : (selectedCustomer.total_sell_due != null ? `$${Number(selectedCustomer.total_sell_due).toFixed(2)}` : '--')}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Total Sell Return Due</strong>
                                            <div>{selectedCustomer.sell_return != null ? `$${Number(selectedCustomer.sell_return).toFixed(2)}` : (selectedCustomer.total_sell_return_due != null ? `$${Number(selectedCustomer.total_sell_return_due).toFixed(2)}` : '--')}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Pay Term</strong>
                                            <div>{selectedCustomer.pay_term_number ? `${selectedCustomer.pay_term_number} ${selectedCustomer.pay_term_type || ''}` : '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Address Section */}
                                <div className="invoice-section">
                                    <h3>Address</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Address Line 1</strong>
                                            <div>{selectedCustomer.address_line_1 || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Address Line 2</strong>
                                            <div>{selectedCustomer.address_line_2 || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>City</strong>
                                            <div>{selectedCustomer.city || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>State</strong>
                                            <div>{selectedCustomer.state || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Country</strong>
                                            <div>{selectedCustomer.country || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Zip Code</strong>
                                            <div>{selectedCustomer.zip_code || '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Custom Fields Section */}
                                {(selectedCustomer.custom_field1 || selectedCustomer.custom_field2 || selectedCustomer.custom_field3 || selectedCustomer.custom_field4 || selectedCustomer.custom_field5) && (
                                    <div className="invoice-section">
                                        <h3>Custom Fields</h3>
                                        <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                            {selectedCustomer.custom_field1 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 1</strong>
                                                    <div>{selectedCustomer.custom_field1}</div>
                                                </div>
                                            )}
                                            {selectedCustomer.custom_field2 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 2</strong>
                                                    <div>{selectedCustomer.custom_field2}</div>
                                                </div>
                                            )}
                                            {selectedCustomer.custom_field3 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 3</strong>
                                                    <div>{selectedCustomer.custom_field3}</div>
                                                </div>
                                            )}
                                            {selectedCustomer.custom_field4 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 4</strong>
                                                    <div>{selectedCustomer.custom_field4}</div>
                                                </div>
                                            )}
                                            {selectedCustomer.custom_field5 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 5</strong>
                                                    <div>{selectedCustomer.custom_field5}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-btn close-btn" style={{ background: '#6366f1', color: 'white' }} onClick={() => { handleCloseModal(); handleEditCustomer(selectedCustomer.id); }}>Edit</button>
                            <button className="modal-btn close-btn" onClick={handleCloseModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}

        {/* Pay Modal */}
        {showPayModal && selectedCustomer && (
            <div className="modal-overlay" onClick={() => setShowPayModal(false)}>
                <div className="modal-container" style={{ maxWidth: '700px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Add payment</h2>
                        <button className="modal-close-btn" onClick={() => setShowPayModal(false)}>✕</button>
                    </div>
                    <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        
                        {/* Top Info Cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                            <div style={{ background: 'var(--pay-card-bg, #f8fafc)', border: '1px solid var(--pay-card-border, #e2e8f0)', borderRadius: '4px', padding: '1rem', fontSize: '0.9rem', color: 'var(--pay-card-text, #1e293b)' }}>
                                <div style={{ marginBottom: '0.25rem' }}><strong>Customer:</strong> {selectedCustomer.name || '--'}</div>
                                <div><strong>Business:</strong> {selectedCustomer.supplier_business_name || selectedCustomer.business_name || '--'}</div>
                            </div>
                            <div style={{ background: 'var(--pay-card-bg, #f8fafc)', border: '1px solid var(--pay-card-border, #e2e8f0)', borderRadius: '4px', padding: '1rem', fontSize: '0.9rem', color: 'var(--pay-card-text, #1e293b)', lineHeight: '1.5' }}>
                                <div><strong>Total Sell:</strong> ${Number(selectedCustomer.total_sell || 0).toFixed(2)}</div>
                                <div><strong>Total Paid:</strong> ${Number(selectedCustomer.total_paid || 0).toFixed(2)}</div>
                                <div><strong>Total Sell Due:</strong> ${Number(selectedCustomer.total_sell_due || selectedCustomer.sell_due || 0).toFixed(2)}</div>
                                <div><strong>Credit Limit:</strong> ${selectedCustomer.credit_limit ? Number(selectedCustomer.credit_limit).toFixed(2) : '0.00'}</div>
                                <div><strong>Opening Balance:</strong> ${Number(selectedCustomer.opening_balance || 0).toFixed(2)}</div>
                                <div><strong>Opening Balance Due:</strong> ${Number(selectedCustomer.opening_balance || 0).toFixed(2)}</div>
                            </div>
                        </div>

                        {/* Form Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Payment Method:*</label>
                                <div style={{ display: 'flex', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: 'var(--pay-icon-bg, #f1f5f9)', borderRight: '1px solid var(--pay-icon-border, #cbd5e1)', color: 'var(--pay-icon-color, #64748b)' }}><i className="fa fa-money-bill-wave"></i></span>
                                    <select value={payMethod} onChange={e => setPayMethod(e.target.value)} style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }} disabled={isLoadingPayMethods}>
                                        {isLoadingPayMethods ? (
                                            <option value="" disabled>Loading payment methods...</option>
                                        ) : (
                                            paymentMethods.map(m => (
                                                <option key={m.id} value={m.name || m.id}>{m.name}</option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Paid on:*</label>
                                <div style={{ display: 'flex', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: 'var(--pay-icon-bg, #f1f5f9)', borderRight: '1px solid var(--pay-icon-border, #cbd5e1)', color: 'var(--pay-icon-color, #64748b)' }}><i className="fa fa-calendar"></i></span>
                                    <input type="datetime-local" defaultValue={dateUtils.getChicagoISOString()} style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }} />
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Amount:*</label>
                                <div style={{ display: 'flex', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: 'var(--pay-icon-bg, #f1f5f9)', borderRight: '1px solid var(--pay-icon-border, #cbd5e1)', color: 'var(--pay-icon-color, #64748b)' }}><i className="fa fa-money-bill"></i></span>
                                    <input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }} />
                                </div>
                            </div>
                        </div>

                        {/* Bottom Form Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Attach Document:</label>
                                <input type="file" style={{ width: '100%', fontSize: '0.85rem', color: 'var(--pay-input-text, inherit)' }} />
                                <div style={{ fontSize: '0.75rem', color: 'var(--pay-helper-text, #64748b)', marginTop: '0.5rem' }}>Allowed File: .pdf, .csv, .zip, .doc, .docx, .jpeg, .jpg, .png</div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Payment Account:</label>
                                <div style={{ display: 'flex', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: 'var(--pay-icon-bg, #f1f5f9)', borderRight: '1px solid var(--pay-icon-border, #cbd5e1)', color: 'var(--pay-icon-color, #64748b)' }}><i className="fa fa-university"></i></span>
                                    <select value={payAccount} onChange={e => setPayAccount(e.target.value)} style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }} disabled={isLoadingPayAccounts}>
                                        <option value="">None</option>
                                        {isLoadingPayAccounts ? (
                                            <option value="" disabled>Loading payment accounts...</option>
                                        ) : (
                                            paymentAccounts.map(acc => (
                                                <option key={acc.id} value={acc.id}>
                                                    {acc.name || acc.account_name || acc.display_name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                                <div style={{ marginTop: '1rem' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--pay-label-text, #1e293b)', display: 'block', marginBottom: '0.4rem' }}>Payment Note:</label>
                                    <textarea rows={2} value={payNote} onChange={e => setPayNote(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--pay-icon-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', background: 'var(--pay-input-bg, white)', color: 'var(--pay-input-text, inherit)' }} placeholder="Optional note" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer" style={{ padding: '1.25rem 2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                        <button style={{ padding: '0.6rem 1.75rem', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }} onClick={() => { alert('Payment saved'); setShowPayModal(false); }}>Save</button>
                        <button style={{ padding: '0.6rem 1.75rem', background: '#334155', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }} onClick={() => setShowPayModal(false)}>Close</button>
                    </div>
                </div>
            </div>
        )}

        {/* Ledger Modal */}
        {showLedgerModal && selectedCustomer && (
            <LedgerModal
                sale={{ contact_id: selectedCustomer.id, customer: selectedCustomer.name }}
                onClose={() => { setShowLedgerModal(false); setSelectedCustomer(null); }}
            />
        )}

        {/* Documents and Note Modal */}
        {showDocModal && selectedCustomer && (
            <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
                <div className="modal-container" style={{ maxWidth: '500px', width: '95%' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header"><h2>Documents and Note - {selectedCustomer.name}</h2><button className="modal-close-btn" onClick={() => setShowDocModal(false)}>x</button></div>
                    <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div><label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Note</label>
                            <textarea rows={4} value={docNote} onChange={e => setDocNote(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem', resize: 'vertical' }} placeholder="Add a note..." /></div>
                        <div><label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Attach Document</label>
                            <input type="file" style={{ width: '100%' }} /></div>
                    </div>
                    <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '1rem 2rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button style={{ padding: '0.5rem 1.25rem', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => { alert('Saved (demo)'); setShowDocModal(false); }}>Save</button>
                        <button style={{ padding: '0.5rem 1.25rem', background: '#64748b', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowDocModal(false)}>Cancel</button>
                    </div>
                </div>
            </div>
        )}

        {/* ── Purchases Modal ── */}
        {showPurchasesModal && selectedCustomer && (
            <ContactPurchasesModal
                type="customer"
                contact={selectedCustomer}
                onClose={() => setShowPurchasesModal(false)}
            />
        )}

        <ConfirmModal
            isOpen={confirmModal.isOpen}
            title={confirmModal.action === 'delete' ? 'Delete Customer' : confirmModal.data?.contact_status === 'inactive' ? 'Activate Customer' : 'Deactivate Customer'}
            message={confirmModal.action === 'delete' ? 'Are you sure you want to delete this customer?' : `Are you sure you want to ${confirmModal.data?.contact_status === 'inactive' ? 'activate' : 'deactivate'} this customer?`}
            onConfirm={confirmAction}
            onCancel={() => setConfirmModal({ isOpen: false, action: '', data: null })}
            confirmText={confirmModal.action === 'delete' ? 'Delete' : confirmModal.data?.contact_status === 'inactive' ? 'Activate' : 'Deactivate'}
        />
        </div>
    );
};

export default Customers;
